import { EMPTY } from "../constants/global";
import {
	KNIT_BEANIE_INPUT,
	MICRO_BEANIE_INPUT,
	OUTLINE_INPUT,
	SCRIPT_TEXT_FONT_SELECTOR,
	SCRIPT_TEXT_NOT_SELECT,
	SCRIPT_TEXT_OPTIONS,
	STYLE_ACTIVE,
} from "../constants/selectors";
import disableInput from "../dom/disableInput";
import enableInput from "../dom/enableInput";
import hideOutline from "../dom/hideOutline";
import resetInputValue from "../dom/resetInputValue";
import resetOutline from "../dom/resetOutline";
import selectAllElements from "../dom/selectAllElements";
import selectSingleElement from "../dom/selectSingleElement";
import { showOrHideElements } from "../dom/showOrHideElements";
import isNeutraFont from "../utils/isNeutraFont";
import {
	BOTTOMSUP_LETTERS_MICROSCRIPT_OPTION,
	BOTTOM_LETTERS_INPUT,
	BOTTOM_LETTERS_WRAPPER_SELECTOR,
	BUBBLE_LETTERS_MICROBEANIE_OPTION,
	BUBBLE_LETTERS_MICROSCRIPT_OPTION,
	JOURNEY_LETTERS_MICROBEANIE_OPTION,
	JOURNEY_LETTERS_MICROSCRIPT_OPTION,
	KNIT_BEANIE_TEXT,
	MICROSCRIPT_TEXT,
	MICROSCRIPT_TEXT_INPUT,
	MICRO_BEANIE_TEXT,
	MICRO_CHECKBOX,
	OUTLINE_WRAPPER_SELECTOR,
	PROSHOP_LETTERS_MICROSCRIPT_OPTION,
	SCRIPT_TEXT,
	SECONDARY_LETTERS,
	SECONDARY_LETTERS_WRAPPER_SELECTOR,
} from "./_selectors";
import generateCanvas from "./generateCanvas";

const snapbackName = "Snapback";
const knitBeanieName = "Knit Beanies";

function microSetup() {
	const microCheckbox = selectSingleElement<HTMLInputElement>(MICRO_CHECKBOX);

	if (microCheckbox !== null) {
		microCheckbox.addEventListener("change", function () {
			const activeStyle =
				selectSingleElement<HTMLSpanElement>(STYLE_ACTIVE)?.dataset.value ?? snapbackName;
			console.log("active style", activeStyle);
			const scriptText = selectSingleElement<HTMLInputElement>(SCRIPT_TEXT);
			const scriptTextFonts = selectAllElements<HTMLSpanElement>(SCRIPT_TEXT_FONT_SELECTOR);
			const microscriptText = selectAllElements<HTMLInputElement>(MICROSCRIPT_TEXT);
			const microScriptInput = selectSingleElement<HTMLInputElement>(MICROSCRIPT_TEXT_INPUT);
			const microbeanieText = selectAllElements<HTMLInputElement>(MICRO_BEANIE_TEXT);
			const microbeanieInput = selectSingleElement<HTMLInputElement>(MICRO_BEANIE_INPUT);
			const knitBeanieText = selectAllElements<HTMLInputElement>(KNIT_BEANIE_TEXT);
			const knitBeanieTextInput = selectSingleElement<HTMLInputElement>(KNIT_BEANIE_INPUT);
			const scriptTextOptions = selectSingleElement(SCRIPT_TEXT_OPTIONS);
			const monogramInput = selectSingleElement<HTMLInputElement>(MICROSCRIPT_TEXT_INPUT);
			const topLetters = selectSingleElement<HTMLInputElement>(SECONDARY_LETTERS);
			const topLettersWrapper = selectSingleElement(SECONDARY_LETTERS_WRAPPER_SELECTOR);
			const bottomLettersInput = selectSingleElement<HTMLInputElement>(BOTTOM_LETTERS_INPUT);
			const bottomLettersWrapper = selectSingleElement(BOTTOM_LETTERS_WRAPPER_SELECTOR);

			const scriptTextInput = selectSingleElement<HTMLInputElement>(SCRIPT_TEXT_NOT_SELECT);
			const outline = selectSingleElement<HTMLInputElement>(OUTLINE_INPUT);
			const microBubbleFont = selectSingleElement<HTMLSpanElement>(
				BUBBLE_LETTERS_MICROSCRIPT_OPTION,
			);
			const microJourneyFont = selectSingleElement<HTMLSpanElement>(
				JOURNEY_LETTERS_MICROSCRIPT_OPTION,
			);
			const microBottomsupFont = selectSingleElement<HTMLSpanElement>(
				BOTTOMSUP_LETTERS_MICROSCRIPT_OPTION,
			);
			const microProshopFont = selectSingleElement<HTMLSpanElement>(
				PROSHOP_LETTERS_MICROSCRIPT_OPTION,
			);
			const microBeanieBubbleFont = selectSingleElement<HTMLSpanElement>(
				BUBBLE_LETTERS_MICROBEANIE_OPTION,
			);
			const microBeanieJourneyFont = selectSingleElement<HTMLSpanElement>(
				JOURNEY_LETTERS_MICROBEANIE_OPTION,
			);

			// TODO: The folowing code only takes into account microscript text. We need to take into account the microbeanie text as well.
			if (microCheckbox.checked) {
				// NOTE: Specific to each micro input
				if (activeStyle !== knitBeanieName) {
					// not micro beanie
					showOrHideElements({
						elements: microscriptText,
						show: true,
					});
					showOrHideElements({ elements: microbeanieText, show: false });
					if (topLetters) topLetters.disabled = true;
					disableInput(topLetters, topLettersWrapper);
					disableInput(bottomLettersInput, bottomLettersWrapper);
					// Clear the input values when disabling
					resetInputValue(topLetters);
					resetInputValue(bottomLettersInput);
				} else {
					// micro beanie only
					showOrHideElements({
						elements: microbeanieText,
						show: true,
					});
					showOrHideElements({
						elements: knitBeanieText,
						show: false,
					});
					showOrHideElements({
						elements: microscriptText,
						show: false,
					});
					// Clear the knit beanie input to prevent text overlap
					resetInputValue(knitBeanieTextInput);
				}

				// NOTE: Shared between micro inputs
				showOrHideElements({
					elements: scriptTextOptions,
					show: false,
				});
				showOrHideElements({ elements: scriptText, show: false });
				showOrHideElements({ elements: microBubbleFont, show: false });
				showOrHideElements({ elements: microJourneyFont, show: false });
				showOrHideElements({ elements: microBeanieBubbleFont, show: false });
				showOrHideElements({ elements: microBeanieJourneyFont, show: false });
				showOrHideElements({ elements: microBottomsupFont, show: false });
				showOrHideElements({ elements: microProshopFont, show: false });

				showOrHideElements({ elements: outline, show: false });

				hideOutline();
			} else {
				// NOTE: Specific to eact micro input
				if (activeStyle !== knitBeanieName) {
					// not micro beanie
					showOrHideElements({
						elements: microscriptText,
						show: false,
					});
					showOrHideElements({ elements: microbeanieText, show: false });
					showOrHideElements({
						elements: [scriptText, ...scriptTextFonts],
						show: true,
						displayValue: "flex",
					});
					if (!isNeutraFont()) {
						showOrHideElements({
							elements: [outline],
							show: true,
							displayValue: "flex",
						});
						showOrHideElements({
							elements: [selectSingleElement(OUTLINE_WRAPPER_SELECTOR)],
							show: true,
						});
						resetOutline();
					} else {
						hideOutline();
					}
					if (topLetters) topLetters.disabled = false;
					enableInput(topLetters, topLettersWrapper);
					enableInput(bottomLettersInput, bottomLettersWrapper);
				} else {
					// micro beanie only (when unchecked, showing regular beanie)
					showOrHideElements({
						elements: microbeanieText,
						show: false,
					});
					showOrHideElements({
						elements: knitBeanieText,
						show: true,
					});
					showOrHideElements({
						elements: microscriptText,
						show: true,
					});
					// Clear the micro beanie input when switching back to regular beanie
					resetInputValue(microbeanieInput);
				}

				// NOTE: Shared between micro inputs
				showOrHideElements({
					elements: scriptTextOptions,
					show: true,
					displayValue: "flex",
				});
				showOrHideElements({
					elements: microBubbleFont,
					show: true,
					displayValue: "flex",
				});
				showOrHideElements({
					elements: microJourneyFont,
					show: true,
					displayValue: "flex",
				});
				showOrHideElements({
					elements: microBeanieBubbleFont,
					show: true,
					displayValue: "flex",
				});
				showOrHideElements({
					elements: microBeanieJourneyFont,
					show: true,
					displayValue: "flex",
				});

				resetOutline();
			}

			if (monogramInput) monogramInput.value = EMPTY;
			if (bottomLettersInput) bottomLettersInput.value = EMPTY;
			if (scriptTextInput) scriptTextInput.value = EMPTY;
			if (knitBeanieTextInput) knitBeanieTextInput.value = EMPTY;
			if (microbeanieInput) microbeanieInput.value = EMPTY;
			if (microScriptInput) microScriptInput.value = EMPTY;
			if (outline) outline.value = EMPTY;

			generateCanvas({
				color: "transparent",
				text: "",
				id: "outline",
				withStroke: true,
			});
		});
	}
}

export default microSetup;
